// Spaltenwahl (Agent 5B, Runde 5): Popover am Knopf „Spalten“ der Bedienleiste. Oben die gezeigten Spalten in ihrer Reihenfolge
// (ziehen oder mit den Pfeilen verschieben, entfernen), darunter alle wählbaren Spalten in Gruppen mit Suche. „Übernehmen“ merkt die
// Wahl je Nutzer und Liste; „Standard“ stellt die Spalten der Liste bzw. der Smart View wieder her.
import { useEffect, useMemo, useRef, useState } from "react";
import { Ausklapp } from "../Ausklapp";
import { Icon } from "../Icon";
import { feldTrifft, gleicheListe, gruppiere } from "./BedienleisteLogik";
import type { ListenSpalte } from "./listenVertrag";
import "./Bedienleiste.css";

interface Props {
  anker: HTMLElement | null;
  offen: boolean;
  onSchliessen: () => void;
  felder: ListenSpalte[];
  gezeigt: string[];
  /** Spalten der Liste ohne eigene Wahl; ohne Angabe gibt es kein „Standard“ */
  standard?: string[];
  /** null = Standard der Liste */
  onSpalten: (keys: string[] | null) => void;
  /** Gruppen, die beim Öffnen aufgeklappt sind (die übrigen sind zu, bis man sucht) */
  offeneGruppen?: string[];
}

function Griff() {
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor" aria-hidden="true">
      <circle cx="2.5" cy="3" r="1.1" />
      <circle cx="7.5" cy="3" r="1.1" />
      <circle cx="2.5" cy="7" r="1.1" />
      <circle cx="7.5" cy="7" r="1.1" />
      <circle cx="2.5" cy="11" r="1.1" />
      <circle cx="7.5" cy="11" r="1.1" />
    </svg>
  );
}

export function SpaltenWahl({ anker, offen, onSchliessen, felder, gezeigt, standard, onSpalten, offeneGruppen = ["Lead", "Eigene Felder"] }: Props) {
  const [entwurf, setEntwurf] = useState<string[]>(gezeigt);
  const [suche, setSuche] = useState("");
  const [zu, setZu] = useState<Set<string>>(new Set());
  const [zieht, setZieht] = useState<string | null>(null);
  const [ziel, setZiel] = useState<string | null>(null);
  const eingabe = useRef<HTMLInputElement>(null);
  const gezeigtRef = useRef(gezeigt);
  gezeigtRef.current = gezeigt;

  const gruppenNamen = useMemo(() => gruppiere(felder).map((g) => g.gruppe), [felder]);
  useEffect(() => {
    if (!offen) return;
    setEntwurf(gezeigtRef.current);
    setSuche("");
    setZu(new Set(gruppenNamen.filter((g) => !offeneGruppen.includes(g))));
    window.setTimeout(() => eingabe.current?.focus(), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);

  const feld = (key: string) => felder.find((f) => f.key === key);
  const festeKeys = useMemo(() => new Set(felder.filter((f) => f.fest).map((f) => f.key)), [felder]);
  const suchend = suche.trim().length > 0;
  const treffer = useMemo(() => (suchend ? felder.filter((f) => feldTrifft(f, suche)) : felder), [felder, suche, suchend]);
  const gruppen = useMemo(() => gruppiere(treffer), [treffer]);

  const umschalten = (key: string) => {
    if (festeKeys.has(key)) return;
    setEntwurf((e) => (e.includes(key) ? e.filter((k) => k !== key) : [...e, key]));
  };
  const verschieben = (key: string, nach: number) => {
    setEntwurf((e) => {
      const von = e.indexOf(key);
      const ziel_ = Math.max(0, Math.min(e.length - 1, nach));
      if (von < 0 || von === ziel_) return e;
      const kopie = [...e];
      kopie.splice(von, 1);
      kopie.splice(ziel_, 0, key);
      // feste Spalten (Name) bleiben vorn
      const fest = kopie.filter((k) => festeKeys.has(k));
      return [...fest, ...kopie.filter((k) => !festeKeys.has(k))];
    });
  };

  const aenderung = !gleicheListe(entwurf, gezeigt);
  const istStandard = !!standard && gleicheListe(entwurf, standard);

  const uebernehmen = () => {
    onSpalten(standard && gleicheListe(entwurf, standard) ? null : entwurf);
    onSchliessen();
  };

  return (
    <Ausklapp anker={anker} offen={offen} onSchliessen={onSchliessen} ausrichtung="end" breite={340} maxHoehe={560} rolle="dialog">
      <div
        className="bl-pop"
        role="dialog"
        aria-label="Spalten"
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            uebernehmen();
          }
        }}
      >
        <label className="bl-pop__suche">
          <Icon name="suche" groesse={14} />
          <input ref={eingabe} value={suche} placeholder="Spalte suchen …" aria-label="Spalte suchen" onChange={(e) => setSuche(e.target.value)} />
        </label>
        <div className="bl-pop__liste">
          {!suchend ? (
            <>
              <div className="bl-pop__ueberschrift">Gezeigt ({entwurf.length})</div>
              {entwurf.map((key, i) => {
                const f = feld(key);
                const fest = festeKeys.has(key);
                return (
                  <div
                    key={key}
                    className={`bl-gezeigt__zeile${zieht === key ? " bl-gezeigt__zeile--zieht" : ""}${ziel === key && zieht && zieht !== key ? " bl-gezeigt__zeile--ziel" : ""}`}
                    draggable={!fest}
                    onDragStart={(e) => {
                      setZieht(key);
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", key);
                    }}
                    onDragOver={(e) => {
                      if (!zieht || festeKeys.has(key)) return;
                      e.preventDefault();
                      setZiel(key);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (zieht && zieht !== key && !festeKeys.has(key)) verschieben(zieht, entwurf.indexOf(key));
                      setZieht(null);
                      setZiel(null);
                    }}
                    onDragEnd={() => {
                      setZieht(null);
                      setZiel(null);
                    }}
                  >
                    <span className={`bl-gezeigt__griff${fest ? " bl-gezeigt__griff--fest" : ""}`} aria-hidden="true" title={fest ? undefined : "Ziehen zum Verschieben"}>
                      <Griff />
                    </span>
                    <span className="bl-gezeigt__name" title={f?.label ?? key}>
                      {f?.label || key}
                    </span>
                    <button type="button" className="bl-gezeigt__knopf" disabled={fest || i === 0 || (i === 1 && festeKeys.has(entwurf[0]))} aria-label={`${f?.label ?? key} nach vorn`} title="Nach vorn" onClick={() => verschieben(key, i - 1)}>
                      <Icon name="pfeil-hoch" groesse={12} />
                    </button>
                    <button type="button" className="bl-gezeigt__knopf" disabled={fest || i === entwurf.length - 1} aria-label={`${f?.label ?? key} nach hinten`} title="Nach hinten" onClick={() => verschieben(key, i + 1)}>
                      <Icon name="pfeil-runter" groesse={12} />
                    </button>
                    <button type="button" className="bl-gezeigt__knopf" disabled={fest} aria-label={`${f?.label ?? key} entfernen`} title={fest ? "Diese Spalte bleibt immer" : "Entfernen"} onClick={() => umschalten(key)}>
                      <Icon name="schliessen" groesse={12} />
                    </button>
                  </div>
                );
              })}
              <div className="bl-pop__ueberschrift" style={{ marginTop: 6 }}>
                Alle Spalten
              </div>
            </>
          ) : null}
          {gruppen.length === 0 ? <div className="bl-pop__leer">Keine Spalte gefunden.</div> : null}
          {gruppen.map((g) => {
            const auf = suchend || !zu.has(g.gruppe);
            const gewaehlt = g.felder.filter((f) => entwurf.includes(f.key)).length;
            return (
              <div key={g.gruppe}>
                <button
                  type="button"
                  className="bl-pop__gruppe"
                  aria-expanded={auf}
                  onClick={() => {
                    if (suchend) return;
                    setZu((alt) => {
                      const neu = new Set(alt);
                      if (neu.has(g.gruppe)) neu.delete(g.gruppe);
                      else neu.add(g.gruppe);
                      return neu;
                    });
                  }}
                >
                  <Icon name={auf ? "chevron-unten" : "chevron-rechts"} groesse={12} />
                  {g.gruppe}
                  <span className="bl-pop__gruppe-zahl">{gewaehlt ? `${gewaehlt}/${g.felder.length}` : g.felder.length}</span>
                </button>
                {auf
                  ? g.felder.map((f) => {
                      const an = entwurf.includes(f.key);
                      return (
                        <button
                          key={f.key}
                          type="button"
                          className={`bl-eintrag${f.fest ? " bl-eintrag--fest" : ""}`}
                          role="checkbox"
                          aria-checked={an}
                          disabled={f.fest}
                          title={f.fest ? "Diese Spalte bleibt immer" : undefined}
                          onClick={() => umschalten(f.key)}
                        >
                          <input type="checkbox" checked={an} readOnly tabIndex={-1} aria-hidden="true" />
                          <span className="bl-eintrag__text">{f.label}</span>
                        </button>
                      );
                    })
                  : null}
              </div>
            );
          })}
        </div>
        <div className="bl-pop__fuss">
          {standard ? (
            <button type="button" className="bl-pop__link" disabled={istStandard} onClick={() => setEntwurf(standard)} title="Spalten der Liste wiederherstellen">
              Standard
            </button>
          ) : null}
          <span className="bl-pop__rechts">
            <button type="button" className="knopf knopf--klein" onClick={onSchliessen}>
              Abbrechen
            </button>
            <button type="button" className="knopf knopf--klein knopf--primaer" disabled={!aenderung} onClick={uebernehmen}>
              Übernehmen
            </button>
          </span>
        </div>
      </div>
    </Ausklapp>
  );
}
